import { INSTITUTION_STATUS_FORM_VALUES } from "../constants/institutionStatus";
import type { InstitutionFormValues } from "../schemas/institution-form.schema";
import type {
  InstitutionDetail,
  InstitutionListRow,
  InstitutionsPagination,
  TenantOption,
} from "../types/institution.types";

export function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

export function pickString(v: unknown): string {
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}

/**
 * Finds the list array in a response. Accepts a root object with an array under
 * any of `keys`, an optional `{ success, data }` envelope (data = the same
 * object or the array itself). Pagination is read from the same level.
 */
export function unwrapListBody(
  body: unknown,
  keys: string[],
): { items: unknown[]; pagination: unknown } {
  const root = asRecord(body);
  if (!root) return { items: [], pagination: undefined };
  for (const key of keys) {
    const list = root[key];
    if (Array.isArray(list)) return { items: list, pagination: root.pagination };
  }
  const data = asRecord(root.data);
  if (data) {
    for (const key of keys) {
      const list = data[key];
      if (Array.isArray(list)) {
        return { items: list, pagination: data.pagination ?? root.pagination };
      }
    }
  }
  if (Array.isArray(root.data)) {
    return { items: root.data, pagination: root.pagination };
  }
  return { items: [], pagination: undefined };
}

/** GET /api/v1/institutions: `{ institutions, pagination }`; also `items`/`data`/`results`. */
export function unwrapInstitutionsListBody(body: unknown) {
  return unwrapListBody(body, ["institutions", "items", "data", "results"]);
}

/** Text for a board/medium cell: a string, or an array joined with ", ". "" when missing. */
function joinDisplay(v: unknown): string {
  if (typeof v === "string") return v.trim();
  if (Array.isArray(v)) {
    return v
      .map((entry) => {
        if (typeof entry === "string") return entry.trim();
        const rec = asRecord(entry);
        return rec ? pickString(rec.name).trim() : "";
      })
      .filter(Boolean)
      .join(", ");
  }
  return "";
}

/** Splits a string / list of strings or `{ name }` objects into trimmed, non-empty values. */
function toDisplayList(v: unknown): string[] {
  return joinDisplay(v)
    .split(", ")
    .filter(Boolean);
}

/**
 * Board + medium pairs of an institution. Reads `boards: [{ board, medium }]`;
 * older records that only carry flat `board` / `medium` (string or list) are
 * paired by position, reusing the first value when one side is shorter.
 */
export function toBoardEntries(
  raw: Record<string, unknown>,
): InstitutionFormValues["boards"] {
  if (Array.isArray(raw.boards)) {
    const entries = raw.boards
      .map((entry) => {
        const rec = asRecord(entry);
        return {
          board: pickString(rec?.board ?? rec?.board_name).trim(),
          medium: pickString(rec?.medium ?? rec?.medium_name).trim(),
        };
      })
      .filter((entry) => entry.board || entry.medium);
    if (entries.length > 0) return entries;
  }
  const boards = toDisplayList(raw.board);
  const mediums = toDisplayList(raw.medium);
  const count = Math.max(boards.length, mediums.length);
  return Array.from({ length: count }, (_unused, i) => ({
    board: boards[i] ?? boards[0] ?? "",
    medium: mediums[i] ?? mediums[0] ?? "",
  }));
}

const unique = (values: string[]) => Array.from(new Set(values.filter(Boolean)));

export function mapInstitutionApiItemToRow(
  raw: unknown,
  index: number,
): InstitutionListRow {
  const item = asRecord(raw) ?? {};
  const entries = toBoardEntries(item);
  return {
    id: pickString(item.id) || `institution-${index}`,
    instituteName: pickString(item.institute_name),
    instituteCode: pickString(item.institute_code),
    board: unique(entries.map((e) => e.board)).join(", "),
    medium: unique(entries.map((e) => e.medium)).join(", "),
    status: pickString(item.status).trim().toLowerCase(),
    createdAt: pickString(item.created_at),
  };
}

function toPositiveNumber(v: unknown): number | null {
  const n = typeof v === "string" && v.trim() === "" ? NaN : Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function mapInstitutionsPagination(
  raw: unknown,
  fallback: { page: number; pageSize: number; rowCount: number },
): InstitutionsPagination {
  const p = asRecord(raw) ?? {};
  const page = toPositiveNumber(p.page) || fallback.page;
  const pageSize = toPositiveNumber(p.page_size) || fallback.pageSize;
  const total = toPositiveNumber(p.total) ?? fallback.rowCount;
  const totalPages =
    toPositiveNumber(p.total_pages) ??
    Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  return { page, pageSize, total, totalPages };
}

/**
 * GET /api/v1/institutions/:id shape is not specified: accept a bare object,
 * `{ institution }`, `{ success, data }` or `{ data: { institution } }`.
 */
export function unwrapInstitutionDetailBody(
  body: unknown,
): Record<string, unknown> | null {
  const root = asRecord(body);
  if (!root) return null;
  const data = asRecord(root.data);
  const candidates = [
    root,
    data,
    asRecord(root.institution),
    asRecord(data?.institution),
  ];
  for (const c of candidates) {
    if (c && ("id" in c || "institute_name" in c)) return c;
  }
  return null;
}

/** `YYYY-MM-DD` prefix of an ISO date / datetime, else "". */
function toDateInput(v: unknown): string {
  const s = pickString(v).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
}

/** 0 and null are treated as "not set" for the optional class range. */
function toClassInput(v: unknown): string {
  const s = pickString(v).trim();
  return s === "0" ? "" : s;
}

/** Status dropdown value; statuses without an option (e.g. "deleted") map to "" = unchanged. */
function toFormStatus(v: unknown): string {
  const status = pickString(v).trim().toLowerCase();
  return (INSTITUTION_STATUS_FORM_VALUES as readonly string[]).includes(status)
    ? status
    : "";
}

export function toFormValues(raw: Record<string, unknown>): InstitutionFormValues {
  const tenant = asRecord(raw.tenant);
  return {
    tenant_id: pickString(raw.tenant_id) || pickString(tenant?.id),
    institute_name: pickString(raw.institute_name),
    institute_code: pickString(raw.institute_code),
    institute_type: pickString(raw.institute_type).trim().toLowerCase(),
    status: toFormStatus(raw.status),
    boards: toBoardEntries(raw),
    lowest_class: toClassInput(raw.lowest_class),
    highest_class: toClassInput(raw.highest_class),
    state: pickString(raw.state),
    district: pickString(raw.district),
    block_zone: pickString(raw.block_zone),
    city_village: pickString(raw.city_village),
    pincode: pickString(raw.pincode),
    rural_urban: pickString(raw.rural_urban).trim().toLowerCase(),
    year_established: pickString(raw.year_established),
    management_type: pickString(raw.management_type),
    academic_start: toDateInput(raw.academic_start),
    academic_end: toDateInput(raw.academic_end),
  };
}

export function mapInstitutionApiItemToDetail(
  raw: Record<string, unknown>,
  fallbackId: string,
): InstitutionDetail {
  const row = mapInstitutionApiItemToRow(raw, 0);
  const tenant = asRecord(raw.tenant);
  return {
    ...row,
    id: pickString(raw.id) || fallbackId,
    updatedAt: pickString(raw.updated_at),
    principalName: pickString(raw.principal_name).trim(),
    tenantName:
      pickString(raw.tenant_name) ||
      pickString(tenant?.tenant_name) ||
      pickString(tenant?.name),
    values: toFormValues(raw),
  };
}

/** Tenant lookup response body -> dropdown options labelled "name (code)". */
export function mapTenantOptions(body: unknown): TenantOption[] {
  const { items } = unwrapListBody(body, ["tenants", "items", "data", "results"]);
  const options: TenantOption[] = [];
  for (const raw of items) {
    const item = asRecord(raw);
    if (!item) continue;
    const id = pickString(item.id);
    if (!id) continue;
    const name = pickString(item.tenant_name).trim();
    const code = pickString(item.tenant_code).trim();
    options.push({
      value: id,
      label: name && code ? `${name} (${code})` : name || code || `#${id}`,
    });
  }
  return options;
}
